import { KeyboardEvent, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input, message, Modal, Pagination, Spin, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Empty from '@/components/record/empty';
import SvgIcon from '@/components/SvgIcon';
import { downloadFile } from '@/utils';
import { deleteLog, downloadLog, getLogPage, updateLogTitle } from '@/api/subtitles';
import styles from '@/components/style/index.module.less';
import modalStyles from '@/components/style/modal.module.less';

function Log() {
  const [curr, setCurr] = useState(1);
  const [total, setTotal] = useState(0);
  const [size, setSize] = useState(10);
  const [editingLog, setEditingLog] = useState<API.LogListItem | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  const { t } = useTranslation();

  const getLogPageRequest = useQuery({
    queryKey: ['getLogPage', curr, size],
    queryFn: () => getLogPage(curr, size).then((res) => {
      if (res.data.data.total) {
        setTotal(res.data.data.total);
      }

      return res;
    }),
  });

  const downloadLogRequest = (item: API.LogListItem) => {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return downloadLog({
      user_subtitle_segment_id: item.user_subtitle_segment_id,
      zone,
    }).then((res) => {
      if (res.data.data) {
        const date = dayjs(item.gmt_create).format('YYYY-MM-DD');
        downloadFile(res.data.data, `${item.title}-${date}.txt`);
      }
    }).catch(() => {
      // 导出失败(如后端异常)不再静默吞掉, 给用户提示
      message.error(t('downloadFailed', { defaultValue: '下载失败，请稍后重试' }));
    });
  };
  const onDelete = (id: string) => {
    Modal.confirm({
      className: modalStyles.modal,
      title: t('removeSubtitle'),
      icon: <SvgIcon className={modalStyles.icon} name="danger-solid" />,
      okText: t('common.delete'),
      content: <p>{t('removeSubtitleTips')}</p>,
      onOk: () => deleteLog(id).then(() => {
        getLogPageRequest.refetch();
      }),
    });
  };

  const openTitleEditor = (item: API.LogListItem) => {
    setEditingLog(item);
    setEditingTitle(item.title);
  };

  const closeTitleEditor = () => {
    if (isSavingTitle) return;

    setEditingLog(null);
    setEditingTitle('');
  };

  const saveTitle = async () => {
    if (!editingLog || isSavingTitle) return;

    const title = editingTitle.trim();
    if (!title) {
      message.error(t('titleCannotBeEmpty'));
      return;
    }

    setIsSavingTitle(true);
    try {
      await updateLogTitle({
        user_subtitle_segment_id: editingLog.user_subtitle_segment_id,
        title,
      });
      setEditingLog(null);
      setEditingTitle('');
      await getLogPageRequest.refetch();
    } catch {
      message.error(t('updateTitleFailed'));
    } finally {
      setIsSavingTitle(false);
    }
  };

  const onTitleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    closeTitleEditor();
  };

  const onChange = (page: number, pageSize: number) => {
    setCurr(page);
    setSize(pageSize);
  };
  return (
    <div className={styles.pageContent}>
      <div className={styles.pageTitle}>{t('translationRecord')}</div>
      <div className={styles.translatorlog}>
        {getLogPageRequest.isLoading && <Spin className="!block mx-auto" size="large" />}
        {getLogPageRequest.data?.data.data.records.length === 0 && <Empty text={t('noRecord')} />}
        {getLogPageRequest.data?.data.data.records.map((item) => (
          <div key={item.user_subtitle_segment_id} className={styles.item}>
            <h1>
              <Link
                className={styles.itemTitle}
                to={
                  item.file_merge_id
                    ? `/record/analyse/${item.user_subtitle_segment_id}`
                    : `/record/${item.user_subtitle_segment_id}`
                }
                state={{ from: 'log' }}
              >
                {item.title}
              </Link>
              <Tooltip placement="bottom" title={t('editTitle')}>
                <button
                  aria-label={t('editTitle')}
                  className={styles.titleEditButton}
                  onClick={() => openTitleEditor(item)}
                  type="button"
                >
                  <SvgIcon name="editCussor" />
                </button>
              </Tooltip>
            </h1>
            <time className={styles.time}>{dayjs(item.gmt_create).format('YYYY-MM-DD HH:mm:ss')}</time>
            <div className={styles.action}>
              <Tooltip placement="bottom" title={t('exportSubtitle')}>
                <SvgIcon name="download" onClick={() => downloadLogRequest(item)} />
              </Tooltip>
              <Tooltip placement="bottom" title={t('removeSubtitle')}>
                <SvgIcon name="delete" onClick={() => onDelete(item.user_subtitle_segment_id)} />
              </Tooltip>
            </div>
          </div>
        ))}
        <Pagination
          className={styles.paginationRecord}
          pageSize={size}
          showSizeChanger={false}
          current={curr}
          total={total}
          hideOnSinglePage
          onChange={onChange}
        />
      </div>
      <Modal
        cancelText={t('common.cancel')}
        confirmLoading={isSavingTitle}
        okText={t('common.confirm')}
        onCancel={closeTitleEditor}
        onOk={saveTitle}
        open={Boolean(editingLog)}
        title={t('editTitle')}
      >
        <Input
          autoFocus
          onChange={(event) => setEditingTitle(event.target.value)}
          onKeyDown={onTitleInputKeyDown}
          onPressEnter={saveTitle}
          placeholder={t('editTitlePlaceholder')}
          value={editingTitle}
        />
      </Modal>
    </div>
  );
}

export default Log;
